import { ApiClient } from '../../services/api';
import { CallEndCause, EndReason } from './types';

/**
 * Twilio can prove the browser hung up (`sdk_edge.disconnected_by = "local"`) but never
 * why — a deliberate `Device.disconnectAll()` looks exactly like a clean hangup from
 * outside this process. So each call end is stamped with a cause here and posted to the
 * server to be logged; `EventLogger` does not reach New Relic. Best-effort: reporting
 * never blocks or alters teardown.
 */

const TAB_ID_KEY = 'levitate.aida.tabId';

/**
 * Fallback tab identifier, used as the stored value and when storage is
 * unavailable (private-mode edge cases). Distinct per tab either way.
 */
const fallbackTabId = `tab-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Identifier for this browser tab, stable across in-tab reloads and never shared
 * with another tab — `sessionStorage` is per-tab by definition.
 *
 * This is the field that answers the duplicate-tab question: two distinct
 * `tabId`s reporting against one `phoneCallId` proves more than one tab was
 * driving the same call.
 */
function getTabId(): string {
	try {
		const existing = window.sessionStorage.getItem(TAB_ID_KEY);
		if (existing) return existing;
		window.sessionStorage.setItem(TAB_ID_KEY, fallbackTabId);
		return fallbackTabId;
	} catch {
		return fallbackTabId;
	}
}

export type CallEndReport = {
	cause: CallEndCause;
	reason: EndReason;
	/** True when a call the rep was actually on was torn down — the defect signature. */
	hadActiveCall: boolean;
	phoneCallId?: string;
	callSid?: string;
	callStatus?: string;
	direction?: 'inbound' | 'outbound';
	deviceState?: string;
	errorCode?: number;
};

/**
 * Post a call-end report. Fire-and-forget: a rejected request is swallowed so a
 * diagnostic can never turn into a user-visible failure.
 *
 * Delivery note: every teardown path we are chasing (nav icons, return-to-queue,
 * hashchange, component unmount) keeps this JS context alive, so an ordinary
 * request completes. A tab close or reload can still lose the report — Twilio's
 * `closeProtection` already warns the rep there, and the server-side Twilio
 * callbacks record the call ending regardless.
 */
export function reportCallEnd(apiClient: ApiClient, report: CallEndReport): void {
	// Without a phone-call id there is nothing to correlate the row to, and the
	// endpoint is keyed by it.
	if (!report.phoneCallId) return;

	try {
		void apiClient
			.reportClientCallEnd(report.phoneCallId, {
				cause: report.cause,
				reason: report.reason,
				hadActiveCall: report.hadActiveCall,
				callSid: report.callSid,
				callStatus: report.callStatus,
				direction: report.direction,
				deviceState: report.deviceState,
				errorCode: report.errorCode,
				tabId: getTabId(),
				tabVisibility: typeof document === 'undefined' ? undefined : document.visibilityState,
			})
			.catch(() => {
				/* diagnostics are best-effort */
			});
	} catch {
		/* diagnostics are best-effort */
	}
}
